import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchShopifyInventoryItem, fetchStorjBackupList, deleteStorjBackupObject, resetInventoryCounts, completeAllCounts, seedInventoryTransactionLog, lookupOrderByNumber, probePrinterStatus, fetchPosLabelFormats, printPosLabelFormat } from '../../services/devAPI';
import type { SeedInventoryTransactionLogResponse, StorjBackupObject, PrinterStatusProbeResponse, PosLabelFormatSummary } from '../../types/apiTypes';
import { getApiErrorMessage } from '../../services/errorUtils';
import { OrderContext } from '../../contexts/OrderContext';
import {
  DESTINATION_PRINTERS,
  DEFAULT_PRINTER_ID,
  PRINTER_PROBE_PHYSICAL_STATES,
  PRINTER_PROBE_PHYSICAL_STATE_LABELS,
  type DestinationPrinterId,
  type PrinterProbePhysicalState,
} from '@/shared/types/printer.types';
import './DevPanel.css';

const DEFAULT_INVENTORY_ITEM_ID = '46650990723117';
const MAX_PRINTER_PROBE_RUNS = 6;

interface PrinterProbeRun {
  readonly id: string;
  readonly at: string;
  readonly printerId: DestinationPrinterId;
  readonly printerLabel: string;
  readonly printerIp: string;
  readonly physicalState: PrinterProbePhysicalState;
  readonly queries: PrinterStatusProbeResponse['queries'];
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const DevPanel: React.FC = () => {
  const navigate = useNavigate();
  const orderCtx = useContext(OrderContext);

  const [mockOrderNumber, setMockOrderNumber] = useState<string>('');
  const [mockOrderLoading, setMockOrderLoading] = useState<boolean>(false);
  const [mockOrderError, setMockOrderError] = useState<string | null>(null);

  const [inventoryItemId, setInventoryItemId] = useState<string>(DEFAULT_INVENTORY_ITEM_ID);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [backupObjects, setBackupObjects] = useState<StorjBackupObject[]>([]);
  const [backupLoading, setBackupLoading] = useState<boolean>(false);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<boolean>(false);

  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);

  const [completeLoading, setCompleteLoading] = useState<boolean>(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [completeResult, setCompleteResult] = useState<{ completed: number } | null>(null);
  const [seedLogLoading, setSeedLogLoading] = useState<boolean>(false);
  const [seedLogError, setSeedLogError] = useState<string | null>(null);
  const [seedLogResult, setSeedLogResult] = useState<SeedInventoryTransactionLogResponse | null>(null);

  const [probePrinterId, setProbePrinterId] = useState<DestinationPrinterId>(DEFAULT_PRINTER_ID);
  const [probePhysicalState, setProbePhysicalState] = useState<PrinterProbePhysicalState>('ready');
  const [probeLoading, setProbeLoading] = useState<boolean>(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [probeRuns, setProbeRuns] = useState<readonly PrinterProbeRun[]>([]);

  const [posFormats, setPosFormats] = useState<readonly PosLabelFormatSummary[]>([]);
  const [posFormatsLoaded, setPosFormatsLoaded] = useState<boolean>(false);
  const [posFormatShop, setPosFormatShop] = useState<string>('');
  const [posPrinterId, setPosPrinterId] = useState<DestinationPrinterId>('wifi_printer');
  const [posFormatsLoading, setPosFormatsLoading] = useState<boolean>(false);
  const [posPrintLoading, setPosPrintLoading] = useState<boolean>(false);
  const [posFormatError, setPosFormatError] = useState<string | null>(null);
  const [posFormatSuccess, setPosFormatSuccess] = useState<string | null>(null);

  const handleQuery = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(null);
      const result = await fetchShopifyInventoryItem(inventoryItemId.trim());
      setData(result);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Request failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleListBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupError(null);
      setBackupObjects([]);
      setSelectedKeys(new Set());
      const { objects } = await fetchStorjBackupList();
      setBackupObjects(objects);
    } catch (err: unknown) {
      setBackupError(getApiErrorMessage(err, 'Failed to list backup files'));
    } finally {
      setBackupLoading(false);
    }
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedKeys.size === backupObjects.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(backupObjects.map((o) => o.key)));
    }
  };

  const handleResetInventoryCounts = async () => {
    if (!window.confirm('Are you sure you want to reset all inventory counts? This will truncate cycle_count_events and reset all eligibility fields.')) {
      return;
    }
    try {
      setResetLoading(true);
      setResetError(null);
      setResetSuccess(false);
      await resetInventoryCounts();
      setResetSuccess(true);
    } catch (err: unknown) {
      setResetError(getApiErrorMessage(err, 'Reset failed'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleCompleteAllCounts = async () => {
    if (!window.confirm('Are you sure you want to complete all open counts? This sets actual inventory equal to expected inventory for every incomplete event.')) {
      return;
    }
    try {
      setCompleteLoading(true);
      setCompleteError(null);
      setCompleteResult(null);
      const result = await completeAllCounts();
      setCompleteResult(result);
    } catch (err: unknown) {
      setCompleteError(getApiErrorMessage(err, 'Complete all counts failed'));
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.size === 0) return;
    try {
      setDeleting(true);
      setBackupError(null);
      for (const key of selectedKeys) {
        await deleteStorjBackupObject(key);
      }
      setBackupObjects((prev) => prev.filter((o) => !selectedKeys.has(o.key)));
      setSelectedKeys(new Set());
    } catch (err: unknown) {
      setBackupError(getApiErrorMessage(err, 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSeedInventoryTransactionLog = async () => {
    try {
      setSeedLogLoading(true);
      setSeedLogError(null);
      setSeedLogResult(null);
      const result = await seedInventoryTransactionLog();
      setSeedLogResult(result);
    } catch (err: unknown) {
      setSeedLogError(getApiErrorMessage(err, 'Failed to write placeholder transaction log'));
    } finally {
      setSeedLogLoading(false);
    }
  };

  const handleProbePrinterStatus = async (): Promise<void> => {
    const printer = DESTINATION_PRINTERS.find((entry) => entry.id === probePrinterId);
    if (!printer) return;
    try {
      setProbeLoading(true);
      setProbeError(null);
      const result = await probePrinterStatus(probePrinterId);
      const run: PrinterProbeRun = {
        id: `${Date.now()}-${probePrinterId}-${probePhysicalState}`,
        at: new Date().toISOString(),
        printerId: probePrinterId,
        printerLabel: printer.label,
        printerIp: result.printerIp,
        physicalState: probePhysicalState,
        queries: result.queries,
      };
      setProbeRuns((prev) => [run, ...prev].slice(0, MAX_PRINTER_PROBE_RUNS));
    } catch (err: unknown) {
      setProbeError(getApiErrorMessage(err, 'Failed to probe printer status'));
    } finally {
      setProbeLoading(false);
    }
  };

  const handleLoadPosLabelFormats = async (): Promise<void> => {
    try {
      setPosFormatsLoading(true);
      setPosFormatError(null);
      setPosFormatSuccess(null);
      const { formats } = await fetchPosLabelFormats();
      setPosFormats(formats);
      setPosFormatsLoaded(true);
      setPosFormatShop((current) => {
        if (current && formats.some((format) => format.shop === current)) {
          return current;
        }
        return formats[0]?.shop ?? '';
      });
    } catch (err: unknown) {
      setPosFormatError(getApiErrorMessage(err, 'Failed to load POS label formats'));
    } finally {
      setPosFormatsLoading(false);
    }
  };

  const handlePrintPosLabelFormat = async (): Promise<void> => {
    const shop = posFormatShop.trim();
    if (!shop) return;
    try {
      setPosPrintLoading(true);
      setPosFormatError(null);
      setPosFormatSuccess(null);
      const result = await printPosLabelFormat(shop, posPrinterId);
      const printer = DESTINATION_PRINTERS.find((entry) => entry.id === result.printerId);
      setPosFormatSuccess(
        `Sample printed to ${printer?.label ?? result.printerId} (${result.printerIp}) for ${result.shop}.`,
      );
    } catch (err: unknown) {
      setPosFormatError(getApiErrorMessage(err, 'Failed to print POS label format'));
    } finally {
      setPosPrintLoading(false);
    }
  };

  const handleMockOrderDetails = async () => {
    const trimmed = mockOrderNumber.trim();
    if (!trimmed) return;
    try {
      setMockOrderLoading(true);
      setMockOrderError(null);
      const { orderId } = await lookupOrderByNumber(trimmed);
      if (orderId == null || String(orderId).trim() === '') {
        throw new Error('Order not found');
      }
      orderCtx?.setSelectedOrderId(orderId);
      orderCtx?.setOrderItems([]);
      orderCtx?.setShipToName('');
      navigate(`/order/${orderId}`);
    } catch (err: unknown) {
      setMockOrderError(getApiErrorMessage(err, 'Order not found'));
    } finally {
      setMockOrderLoading(false);
    }
  };

  return (
    <div className="dev-panel-container">
      <h2>Dev Panel</h2>
      <div className="dev-panel-section dev-panel-utilities">
        <Link to="/dev/inv-diff" className="dev-panel-button dev-panel-button-inv-diff">
          Inv Diff Review
        </Link>
        <Link to="/dev/ocr-ab" className="dev-panel-button">
          OCR A/B Test
        </Link>
      </div>

      <div className="dev-panel-section dev-panel-probe">
        <h3 className="dev-panel-section-title">Printer status probe</h3>
        <p className="dev-panel-help">
          Queries only — never prints. Run once with the printer ready, once with the lid open,
          and once with paper out, then compare. Do not print from this panel.
        </p>
        <div className="dev-panel-toggle" role="group" aria-label="Destination printer">
          {DESTINATION_PRINTERS.map((printer) => (
            <button
              key={printer.id}
              type="button"
              className={`dev-panel-toggle-btn${probePrinterId === printer.id ? ' dev-panel-toggle-btn-active' : ''}`}
              aria-pressed={probePrinterId === printer.id}
              onClick={() => setProbePrinterId(printer.id)}
            >
              {printer.label}
            </button>
          ))}
        </div>
        <div className="dev-panel-toggle" role="group" aria-label="Physical printer state">
          {PRINTER_PROBE_PHYSICAL_STATES.map((state) => (
            <button
              key={state}
              type="button"
              className={`dev-panel-toggle-btn${probePhysicalState === state ? ' dev-panel-toggle-btn-active' : ''}`}
              aria-pressed={probePhysicalState === state}
              onClick={() => setProbePhysicalState(state)}
            >
              {PRINTER_PROBE_PHYSICAL_STATE_LABELS[state]}
            </button>
          ))}
        </div>
        <button
          className="dev-panel-button"
          onClick={() => { void handleProbePrinterStatus(); }}
          disabled={probeLoading}
        >
          {probeLoading ? 'Probing...' : 'Probe'}
        </button>
      </div>
      {probeError && <div className="dev-panel-error">{probeError}</div>}
      {probeRuns.length > 0 && (
        <div className="dev-panel-probe-runs">
          {probeRuns.map((run) => (
            <div key={run.id} className="dev-panel-probe-run">
              <div className="dev-panel-probe-run-header">
                {run.printerLabel} ({run.printerIp}) — {PRINTER_PROBE_PHYSICAL_STATE_LABELS[run.physicalState]}
                {' · '}
                {new Date(run.at).toLocaleString()}
              </div>
              <table className="dev-panel-backup-table">
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>Result</th>
                    <th>Reply</th>
                  </tr>
                </thead>
                <tbody>
                  {run.queries.map((query) => (
                    <tr key={`${run.id}-${query.command}`}>
                      <td className="dev-panel-backup-key">{query.command}</td>
                      <td>{query.answered ? 'answered' : 'silent'}</td>
                      <td className="dev-panel-backup-key">
                        {query.answered ? query.ascii || query.hex : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <hr className="dev-panel-divider" />

      <div className="dev-panel-section dev-panel-probe">
        <h3 className="dev-panel-section-title">POS SKU label formats</h3>
        <p className="dev-panel-help">
          Layouts saved from Shopify Admin into Supabase. Load the list, pick a shop format,
          and print a sample through the warehouse relay.
        </p>
        <div className="dev-panel-toggle" role="group" aria-label="Destination printer">
          {DESTINATION_PRINTERS.map((printer) => (
            <button
              key={printer.id}
              type="button"
              className={`dev-panel-toggle-btn${posPrinterId === printer.id ? ' dev-panel-toggle-btn-active' : ''}`}
              aria-pressed={posPrinterId === printer.id}
              onClick={() => setPosPrinterId(printer.id)}
            >
              {printer.label}
            </button>
          ))}
        </div>
        <button
          className="dev-panel-button"
          onClick={() => { void handleLoadPosLabelFormats(); }}
          disabled={posFormatsLoading}
        >
          {posFormatsLoading ? 'Loading...' : 'Load saved formats'}
        </button>
        {posFormatsLoaded && posFormats.length === 0 && (
          <p className="dev-panel-help">No saved label formats in sales.pos_label_formats.</p>
        )}
        {posFormats.length > 0 && (
          <>
            <label htmlFor="pos-label-format-shop">Saved format</label>
            <select
              id="pos-label-format-shop"
              value={posFormatShop}
              onChange={(e) => setPosFormatShop(e.target.value)}
              disabled={posPrintLoading}
            >
              {posFormats.map((format) => (
                <option key={format.shop} value={format.shop}>
                  {format.shop} ({format.widthIn}×{format.heightIn} in, {format.dpi} dpi)
                </option>
              ))}
            </select>
            <button
              className="dev-panel-button"
              onClick={() => { void handlePrintPosLabelFormat(); }}
              disabled={posPrintLoading || !posFormatShop}
            >
              {posPrintLoading ? 'Printing...' : 'Print sample'}
            </button>
          </>
        )}
      </div>
      {posFormatError && <div className="dev-panel-error">{posFormatError}</div>}
      {posFormatSuccess && <div className="dev-panel-success">{posFormatSuccess}</div>}

      <hr className="dev-panel-divider" />

      <div className="dev-panel-section">
        <h3 className="dev-panel-section-title">Mock Order Details</h3>
        <label htmlFor="mock-order-number">Order #</label>
        <input
          id="mock-order-number"
          type="text"
          value={mockOrderNumber}
          onChange={(e) => setMockOrderNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleMockOrderDetails(); }}
          placeholder="e.g. 12345"
          disabled={mockOrderLoading}
        />
        <button
          className="dev-panel-button"
          onClick={handleMockOrderDetails}
          disabled={mockOrderLoading || !mockOrderNumber.trim()}
        >
          {mockOrderLoading ? 'Looking up...' : 'Mock Order Details'}
        </button>
      </div>
      {mockOrderError && <div className="dev-panel-error">{mockOrderError}</div>}

      <hr className="dev-panel-divider" />

      <div className="dev-panel-section">
        <label htmlFor="inventory-item-id">Inventory Item ID</label>
        <input
          id="inventory-item-id"
          type="text"
          value={inventoryItemId}
          onChange={(e) => setInventoryItemId(e.target.value)}
          placeholder="e.g. 46650990723117"
          disabled={loading}
        />
        <button
          className="dev-panel-button"
          onClick={handleQuery}
          disabled={loading || !inventoryItemId.trim()}
        >
          {loading ? 'Querying...' : 'Query Shopify Inventory'}
        </button>
      </div>
      {error && <div className="dev-panel-error">{error}</div>}
      {data && (
        <div className="dev-panel-result">
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}

      <hr className="dev-panel-divider" />

      <div className="dev-panel-section">
        <h3 className="dev-panel-section-title">Storj Backup Bucket</h3>
        <button
          className="dev-panel-button"
          onClick={handleListBackup}
          disabled={backupLoading}
        >
          {backupLoading ? 'Loading...' : 'List Backup Files'}
        </button>
      </div>
      {backupError && <div className="dev-panel-error">{backupError}</div>}
      <hr className="dev-panel-divider" />

      <div className="dev-panel-section">
        <h3 className="dev-panel-section-title">Inventory Counts</h3>
        <button
          className="dev-panel-button dev-panel-button-reset"
          onClick={handleResetInventoryCounts}
          disabled={resetLoading}
        >
          {resetLoading ? 'Resetting...' : 'Reset Inventory Counts'}
        </button>
        <button
          className="dev-panel-button"
          onClick={handleCompleteAllCounts}
          disabled={completeLoading}
        >
          {completeLoading ? 'Completing...' : 'Complete All Counts'}
        </button>
      </div>
      {resetError && <div className="dev-panel-error">{resetError}</div>}
      {resetSuccess && <div className="dev-panel-success">Inventory counts reset successfully.</div>}
      {completeError && <div className="dev-panel-error">{completeError}</div>}
      {completeResult && (
        <div className="dev-panel-success">
          Completed {completeResult.completed} count{completeResult.completed !== 1 ? 's' : ''}.
        </div>
      )}

      <hr className="dev-panel-divider" />

      <div className="dev-panel-section">
        <h3 className="dev-panel-section-title">Inventory Transaction Logs</h3>
        <button
          className="dev-panel-button"
          onClick={handleSeedInventoryTransactionLog}
          disabled={seedLogLoading}
        >
          {seedLogLoading ? 'Writing...' : 'Write Placeholder Log Row'}
        </button>
      </div>
      {seedLogError && <div className="dev-panel-error">{seedLogError}</div>}
      {seedLogResult?.row && (
        <div className="dev-panel-result">
          <pre>{JSON.stringify(seedLogResult, null, 2)}</pre>
        </div>
      )}

      {backupObjects.length > 0 && (
        <div className="dev-panel-backup-section">
          <div className="dev-panel-backup-actions">
            <label className="dev-panel-checkbox-label">
              <input
                type="checkbox"
                checked={selectedKeys.size === backupObjects.length && backupObjects.length > 0}
                onChange={toggleSelectAll}
              />
              Select all
            </label>
            <button
              className="dev-panel-button dev-panel-button-danger"
              onClick={handleDeleteSelected}
              disabled={deleting || selectedKeys.size === 0}
            >
              {deleting ? 'Deleting...' : `Delete selected (${selectedKeys.size})`}
            </button>
          </div>
          <table className="dev-panel-backup-table">
            <thead>
              <tr>
                <th></th>
                <th>Key</th>
                <th>Size</th>
                <th>Last modified</th>
              </tr>
            </thead>
            <tbody>
              {backupObjects.map((obj) => (
                <tr key={obj.key}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedKeys.has(obj.key)}
                      onChange={() => toggleSelect(obj.key)}
                    />
                  </td>
                  <td className="dev-panel-backup-key">{obj.key}</td>
                  <td>{formatBytes(obj.size)}</td>
                  <td>{obj.lastModified ? new Date(obj.lastModified).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DevPanel;
